import { Badge } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import type {
  IAdminParticipantDetail,
  IAdminParticipantHub,
  IAdminParticipantProfile
} from '../../api/participant-contracts';
import type { IOversightResponseRow } from '../../api/oversight-contracts';
import { LIVE } from '../../recycle-bin';

/**
 * The household, as every section of the hub walks it (R7): the participant opened first, then
 * each `household` member in the order survey-api sent them — every other participant an
 * account of the opened one acts for, each a full card. A household of one is the opened
 * participant alone, and nothing about it is drawn differently but the headings: a member's
 * name heads its part of a section only where there is more than one, since on a household of
 * one the page's own title already says whose it is.
 *
 * Nothing here decides who belongs: survey-api grouped the household (one level — the accounts
 * of the one opened), and this file only lays it out.
 */

export function hubMembers(hub: IAdminParticipantHub): IAdminParticipantDetail[] {
  return [hub.participant, ...hub.household];
}

/**
 * One member's part of a section — addressed by `data-member`, its participant id, which is
 * what a spec knows a registrant by and a name is not (two members can share one). Headed by
 * the member's name, its kind, and a mark on the one opened, where the household has more than
 * one; on a household of one it is a plain wrapper.
 */
export function MemberPart({
  member,
  opened,
  multi,
  children
}: {
  member: IAdminParticipantDetail;
  opened: boolean;
  multi: boolean;
  children: React.ReactNode;
}) {
  return (
    <div data-member={member.participantId} className="grid gap-4">
      {multi && (
        <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-[var(--color-royal-blue)]">
          {member.displayName}
          <Badge variant="soft">{member.kind}</Badge>
          {opened && <Badge variant="outline">Opened</Badge>}
        </h3>
      )}
      {children}
    </div>
  );
}

/** Whether no questionnaire has filled any of the profile — survey-api sends every member,
 *  null where nothing filled it, and an object of nulls is how a questionnaire's name is told
 *  from a login standing in for one. */
export function profileIsEmpty(profile: IAdminParticipantProfile): boolean {
  return Object.values(profile).every((value) => value === null);
}

/**
 * Where a participant is — the profile's firm (or, for a team, its team), city and state, as
 * sent and joined; `null` when none of them is filled. Nothing is inferred from the others: a
 * person's firm is the firm their questionnaire named, never the firm participant beside them.
 */
export function profilePlace(profile: IAdminParticipantProfile): Nullable<string> {
  const organisation = profile.firmName ?? profile.teamName;
  const locality = [profile.city, profile.state].filter((part) => part !== null).join(', ');
  const parts = [organisation, locality].filter((part) => part !== null && part !== '');
  return parts.length === 0 ? null : parts.join(' · ');
}

/**
 * A record's responses in the order its page picks a survey's current response by (SR-470's D7,
 * survey-api's recycle bin D7): every live one before every other, so a response in the recycle
 * bin, or archived, stands for a survey only where no live one exists — the survey's tab is kept
 * either way. survey-api's order, newest change first, is kept within each.
 */
export function liveFirst(responses: readonly IOversightResponseRow[]): IOversightResponseRow[] {
  return [
    ...responses.filter((row) => LIVE.has(row.status)),
    ...responses.filter((row) => !LIVE.has(row.status))
  ];
}

/** The surveys a set of responses is for, each code once, in the order first met — the hub's
 *  responses are newest change first, so the survey worked on last leads. */
export function surveysOf(responses: IOversightResponseRow[], participantId?: string): string[] {
  const codes = responses
    .filter((row) => participantId === undefined || row.participant.participantId === participantId)
    .map((row) => row.survey.code);
  return [...new Set(codes)];
}
